import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fmtDate, P, type Employee } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { humanize } from '../../../lib/date';
import { openFile } from '../../../lib/files';
import { useDebounced } from '../../../lib/hooks';
import { storage } from '../../../lib/storage';
import { useGo, useParams } from '../../../navigation/nav';
import { useOrgOptions } from '../components/orgOptions';
import { Avatar, Button, DataTable, IconButton, Modal, PageHeader, Row, Screen, Select, StatusBadge, Text, TextField, useToast } from '../../../ui';

const STATUSES = ['onboarding', 'probation', 'active', 'notice', 'exited'];
const SORTS = [
  { value: 'first_name', label: 'Name' },
  { value: 'emp_code', label: 'Employee code' },
  { value: 'date_of_joining', label: 'Date of joining' },
  { value: 'status', label: 'Status' },
];
const VIEWS_KEY = 'hrms.hr.employeeViews';

type Filters = { search: string; status: string; department_id: number | ''; location_id: number | ''; designation_id: number | ''; sort: string; dir: 'asc' | 'desc' };
const EMPTY: Filters = { search: '', status: '', department_id: '', location_id: '', designation_id: '', sort: 'first_name', dir: 'asc' };
type SavedView = { name: string; filters: Filters };

/** Saved filter views, persisted per device in AsyncStorage. */
function useSavedViews() {
  const [views, setViews] = useState<SavedView[]>([]);
  useEffect(() => {
    let alive = true;
    void storage.get(VIEWS_KEY).then((raw) => {
      if (!alive || !raw) return;
      try {
        const parsed = JSON.parse(raw) as SavedView[];
        if (Array.isArray(parsed)) setViews(parsed);
      } catch {
        /* ignore corrupt value */
      }
    });
    return () => {
      alive = false;
    };
  }, []);
  const persist = (next: SavedView[]) => {
    setViews(next);
    void storage.set(VIEWS_KEY, next.length ? JSON.stringify(next) : null);
  };
  return {
    views,
    save: (name: string, filters: Filters) => persist([...views.filter((v) => v.name !== name), { name, filters }]),
    remove: (name: string) => persist(views.filter((v) => v.name !== name)),
  };
}

/** Employee directory for HR: search, filters, sort, pagination, saved views, CSV export. */
export function EmployeeListScreen() {
  const { can } = useAuth();
  const go = useGo();
  const toast = useToast();
  const params = useParams<{ status: string }>();
  const [f, setF] = useState<Filters>({ ...EMPTY, status: params.status ?? '' });
  const [page, setPage] = useState(1);
  const [viewName, setViewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const saved = useSavedViews();
  const ds = useDebounced(f.search);
  const org = useOrgOptions();
  useEffect(() => {
    if (params.status !== undefined) setF((x) => ({ ...x, status: params.status ?? '' }));
  }, [params.status]);

  const apiParams = { search: ds || undefined, status: f.status || undefined, department_id: f.department_id || undefined, location_id: f.location_id || undefined, designation_id: f.designation_id || undefined, sort: f.sort, dir: f.dir, page, per_page: 25 };
  const q = useQuery({ queryKey: ['employees', apiParams], queryFn: () => api.employees.list(apiParams), placeholderData: keepPreviousData });

  const set = <K extends keyof Filters>(k: K, v: Filters[K]) => {
    setF({ ...f, [k]: v });
    setPage(1);
    setViewName('');
  };
  const dirty = JSON.stringify({ ...f }) !== JSON.stringify(EMPTY);
  const exportCsv = () =>
    openFile(api.reports.csvPath('headcount'), { format: 'csv', department_id: f.department_id || undefined, location_id: f.location_id || undefined, status: f.status || undefined }).catch(() => toast.error('Could not start the download'));

  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="employee-list">
      <PageHeader
        title="Employees"
        subtitle={q.data ? `${q.data.total.toLocaleString('en-IN')} employees` : undefined}
        actions={
          <>
            <Button icon="download" title="Export CSV" onPress={exportCsv} testID="employees-export" />
            {can(P.EMPLOYEES_MANAGE) && (
              <>
                <Button icon="upload" title="Import" onPress={() => go('BulkImport')} />
                <Button variant="primary" icon="plus" title="Add employee" onPress={() => go('EmployeeNew')} testID="employees-add" />
              </>
            )}
          </>
        }
      />
      <Row gap={8} align="flex-end">
        <TextField containerStyle={{ flexGrow: 2, flexBasis: 220 }} placeholder="Search name, code, email…" accessibilityLabel="Search employees" value={f.search} onChangeText={(s) => set('search', s)} testID="employees-search" />
        <Select containerStyle={{ flexGrow: 1, flexBasis: 150 }} placeholder="All statuses" value={f.status} onChange={(v) => set('status', v)} options={[{ value: '', label: 'All statuses' }, ...STATUSES.map((s) => ({ value: s, label: humanize(s) }))]} />
        <Select<number | ''> containerStyle={{ flexGrow: 1, flexBasis: 170 }} placeholder="All departments" value={f.department_id} onChange={(v) => set('department_id', v)} options={[{ value: '', label: 'All departments' }, ...org.departments]} />
        <Select<number | ''> containerStyle={{ flexGrow: 1, flexBasis: 170 }} placeholder="All locations" value={f.location_id} onChange={(v) => set('location_id', v)} options={[{ value: '', label: 'All locations' }, ...org.locations]} />
        <Select<number | ''> containerStyle={{ flexGrow: 1, flexBasis: 170 }} placeholder="All designations" value={f.designation_id} onChange={(v) => set('designation_id', v)} options={[{ value: '', label: 'All designations' }, ...org.designations]} />
      </Row>
      <Row gap={8} align="center">
        <Text variant="small">Sort</Text>
        <Select containerStyle={{ minWidth: 170 }} value={f.sort} onChange={(v) => set('sort', v)} options={SORTS} />
        <IconButton icon="sort" variant="secondary" label={f.dir === 'asc' ? 'Ascending — switch to descending' : 'Descending — switch to ascending'} onPress={() => set('dir', f.dir === 'asc' ? 'desc' : 'asc')} />
        <Text variant="small">{f.dir === 'asc' ? 'A→Z' : 'Z→A'}</Text>
        <View style={{ flex: 1 }} />
        <Select
          containerStyle={{ minWidth: 170 }}
          placeholder={saved.views.length ? 'Saved views' : 'No saved views'}
          value={viewName}
          disabled={!saved.views.length}
          onChange={(name) => {
            const v = saved.views.find((x) => x.name === name);
            if (v) {
              setF({ ...EMPTY, ...v.filters });
              setPage(1);
              setViewName(name);
            }
          }}
          options={saved.views.map((v) => ({ value: v.name, label: v.name }))}
          testID="employees-views"
        />
        {!!viewName && <IconButton icon="trash" label="Delete saved view" onPress={() => { saved.remove(viewName); setViewName(''); toast.success('View deleted'); }} />}
        <Button icon="plus" size="sm" title="Save view" style={{ alignSelf: 'center' }} disabled={!dirty} onPress={() => { setNewName(''); setSaving(true); }} testID="employees-save-view" />
        {dirty && <Button size="sm" variant="ghost" title="Clear" style={{ alignSelf: 'center' }} onPress={() => { setF(EMPTY); setPage(1); setViewName(''); }} />}
      </Row>
      <DataTable<Employee>
        rows={q.data}
        loading={q.isLoading}
        onPageChange={setPage}
        onRowPress={(e) => go('EmployeeDetail', { id: e.id })}
        emptyTitle="No employees match these filters"
        testID="employees-table"
        columns={[
          {
            key: 'full_name',
            title: 'Employee',
            width: 230,
            flex: 2,
            sortValue: (e) => e.full_name,
            render: (e) => (
              <Row gap={8} wrap={false}>
                <Avatar name={e.full_name} size={28} />
                <View style={{ flexShrink: 1 }}>
                  <Text weight="medium" numberOfLines={1}>{e.full_name}</Text>
                  <Text variant="small" numberOfLines={1}>{e.email}</Text>
                </View>
              </Row>
            ),
          },
          { key: 'emp_code', title: 'Code', width: 90 },
          { key: 'designation', title: 'Designation', width: 150 },
          { key: 'department', title: 'Department', width: 140 },
          { key: 'location', title: 'Location', width: 120 },
          { key: 'date_of_joining', title: 'Joined', width: 110, render: (e) => fmtDate(e.date_of_joining), sortValue: (e) => e.date_of_joining },
          { key: 'status', title: 'Status', width: 110, render: (e) => <StatusBadge status={e.status} />, sortValue: (e) => e.status },
        ]}
      />
      <Modal
        open={saving}
        onClose={() => setSaving(false)}
        title="Save view"
        subtitle="Saves the current filters and sort on this device."
        width={420}
        footer={
          <>
            <Button title="Cancel" onPress={() => setSaving(false)} />
            <Button
              variant="primary"
              title="Save"
              disabled={!newName.trim()}
              onPress={() => {
                saved.save(newName.trim(), f);
                setViewName(newName.trim());
                setSaving(false);
                toast.success('View saved');
              }}
              testID="employees-save-view-confirm"
            />
          </>
        }
      >
        <TextField label="View name" placeholder="e.g. Bengaluru engineering" value={newName} onChangeText={setNewName} autoFocus testID="employees-view-name" />
      </Modal>
    </Screen>
  );
}
